/**
 * ChalkQR — `qrcode.react`'s SVG QR with the chalkRoughen filter applied.
 *
 * The chalk filter pushes pixels around so the QR reads as hand-drawn,
 * matching the rest of the design system. A small "QD" monogram sits
 * centered on a white circular backplate so the filter doesn't break
 * scannability.
 */

import { type CSSProperties } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { FILTER_IDS } from '../../filters';

interface ChalkQRProps {
  /** The full URL the QR code encodes. */
  value: string;
  /** Square edge length in CSS pixels. Default 280. */
  size?: number;
  /** Apply the chalkRoughen filter. Disable for sharper scanning under
   *  poor lighting. Default true. */
  rough?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function ChalkQR({
  value,
  size = 280,
  rough = true,
  className,
  style,
}: ChalkQRProps) {
  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: size,
        height: size,
        background: surfaces.paper,
        padding: 16,
        boxSizing: 'content-box',
        ...style,
      }}
    >
      {/* Mid-error-correction so the centre cutout doesn't break scans. */}
      <QRCodeSVG
        value={value}
        size={size}
        level="M"
        bgColor={surfaces.paper}
        fgColor={surfaces.ink}
        marginSize={0}
        style={{
          display: 'block',
          ...(rough ? { filter: `url(#${FILTER_IDS.chalkRoughen})` } : {}),
        }}
      />
      <CenterMonogram size={size} />
    </div>
  );
}

interface CenterMonogramProps {
  size: number;
}

/** Small QD badge with a white circular backplate so QR scanners still
 *  resolve the centre square. Backplate stays under the QR's
 *  error-correction tolerance (≈10% of area at level M). */
function CenterMonogram({ size }: CenterMonogramProps) {
  const badgeSize = Math.round(size * 0.18);
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: badgeSize,
        height: badgeSize,
        borderRadius: '50%',
        background: surfaces.paper,
        boxShadow: `0 0 0 3px ${surfaces.paper}`,
        display: 'grid',
        placeItems: 'center',
        fontFamily: fontStacks.display,
        color: surfaces.ink,
        fontSize: badgeSize * 0.42,
        letterSpacing: '0.06em',
        fontWeight: 700,
      }}
    >
      QD
    </div>
  );
}
